import { Check, Document, EditFilled, Image } from '@ixigo/icons';
import Button from '@ixigo/iui/Button';
import Chip from '@ixigo/iui/Chip';
import IconButton from '@ixigo/iui/IconButton';
import { useState } from 'react';
import { uploadFile } from '../../../apis/common';
import { useConversation } from '../../../contexts/conversation';
import { ActorType, WidgetName } from '../../../enums/conversation';
import { getFileType } from '../../../utils/conversation';
import { openExternalLink } from '../../../utils/pwa';
import MessageBubble from '../../MessageBubble';
import { MessagePart, WidgetProps } from '../index.types';
import PassengerNameForm from './Form';
import { appendAdditionalFields, isPassengerNameModified } from './helper';
import { Passenger, PassengerNameFormData } from './index.types';
import PassengerName from './PassengerName';
import ReviewModification from './Review';

function PassengerModification({ isLastMessage, message, widgetData }: WidgetProps) {
    const { sendMessage } = useConversation();
    const { actor, createdAt } = message;
    const isActorUser = actor.type === ActorType.USER;
    const data = widgetData.parameters.data as Passenger[];
    const [passengers, setTravellers] = useState<Passenger[]>(isActorUser ? data : appendAdditionalFields(data));
    const [selectedTravellerIndex, setSelectedTravellerIndex] = useState<number | null>(null);
    const [reviewOpen, setReviewOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const updateTraveller = (formData: PassengerNameFormData) => {
        setTravellers((prev) => {
            const updatedTravellers = [...prev];
            updatedTravellers[selectedTravellerIndex!] = { ...prev[selectedTravellerIndex!], ...formData };
            return updatedTravellers;
        });

        setSelectedTravellerIndex(null);
    };

    const handleSubmit = async () => {
        setLoading(true);

        const modifiedPassengers = passengers.filter((passenger) => isPassengerNameModified(passenger));
        const promiseArr = modifiedPassengers.map((pax) => {
            const formData = new FormData();
            formData.append('file', pax.proofDocument!);
            return uploadFile(formData);
        });
        const res = await Promise.all(promiseArr);

        const messageParts: MessagePart[] = [
            {
                isPrivate: false,
                widget: {
                    name: WidgetName.PAX_MODIFICATION,
                    parameters: {
                        data: modifiedPassengers.map((pax, index) => ({
                            ...pax,
                            proofDocument: {
                                name: pax.proofDocument!.name,
                                size: pax.proofDocument!.size,
                                type: getFileType(pax.proofDocument!.type),
                                url: res[index].secure_url,
                            },
                        })),
                    },
                },
            },
        ];

        sendMessage({
            messageParts,
            input: messageParts[0].widget!.parameters.data,
        });

        setLoading(false);
        setReviewOpen(false);
    };

    if (isActorUser)
        return passengers.map((passenger, travellerIndex) => (
            <>
                <MessageBubble
                    key={passenger.id}
                    actor={actor}
                    className='w-full !max-w-96 !py-10'
                    timestamp={travellerIndex === 0 ? createdAt : undefined}
                >
                    <div key={passenger.id} className='flex gap-20 items-center justify-between'>
                        <div className='flex-1'>
                            <PassengerName data={passenger} />
                        </div>
                        <IconButton
                            color='neutral'
                            size='sm'
                            variant='flat'
                            onClick={() => setSelectedTravellerIndex(travellerIndex)}
                        >
                            <Check className='text-primary-500' />
                        </IconButton>
                    </div>
                </MessageBubble>
                {passenger.proofDocument && (
                    <Chip
                        className='w-fit !px-10 !py-5'
                        iconLeft={getFileType(passenger.proofDocument.type) === 'image' ? <Image /> : <Document />}
                        variant='soft'
                        color='neutral'
                        onClick={() => openExternalLink(passenger.proofDocument!.url!)}
                    >
                        {passenger.proofDocument.name}
                    </Chip>
                )}
            </>
        ));

    if (!isLastMessage) return null;

    return (
        <>
            {passengers.map((passenger, travellerIndex) => (
                <MessageBubble key={passenger.id} actor={actor} className='w-full !max-w-96 !py-10'>
                    <div key={passenger.id} className='flex gap-20 items-center justify-between'>
                        <div className='flex-1'>
                            <PassengerName data={passenger} />
                        </div>
                        <IconButton
                            color='neutral'
                            size='sm'
                            variant='flat'
                            onClick={() => setSelectedTravellerIndex(travellerIndex)}
                        >
                            <EditFilled className='text-neutral-600' />
                        </IconButton>
                    </div>
                </MessageBubble>
            ))}
            <Button
                className='w-fit mt-5 !px-20 !py-[8px]'
                variant='outlined'
                disabled={!passengers.some((passenger) => isPassengerNameModified(passenger))}
                onClick={() => setReviewOpen(true)}
            >
                Review Corrections
            </Button>
            <PassengerNameForm
                data={passengers[selectedTravellerIndex!]}
                open={selectedTravellerIndex !== null}
                onClose={() => setSelectedTravellerIndex(null)}
                onSubmit={updateTraveller}
            />
            <ReviewModification
                data={passengers}
                loading={loading}
                open={reviewOpen}
                onClose={() => setReviewOpen(false)}
                onSubmit={handleSubmit}
            />
        </>
    );
}

export default PassengerModification;
